import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

const DEFAULT_PREFS = {
  theme: 'light',
  cardsPerPage: 12,
  showFen: true,
  showTagsOnView: true,
  showTagsInQuiz: false,
  diagramPieceSet:            'cburnett',
  diagramLightSquare:         '#ffffdd',
  diagramDarkSquare:          '#8ba56c',
  diagramBorderColor:         '#000000',
  diagramCoordPosition:       'border',
  diagramFlipped:             false,
  diagramShowSideToMoveDisc:  false,
};

export function AuthProvider({ children }) {
  const [token, setToken]   = useState(null);
  const [user, setUser]     = useState(null);
  const [prefs, setPrefs]   = useState(DEFAULT_PREFS); // loaded once after login
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef(null);

  // ── Token management ──────────────────────────────────────────────────────

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => silentRefresh(), 120 * 60 * 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTokens = useCallback(async (accessToken) => {
    setToken(accessToken);
    scheduleRefresh(accessToken);
    // Load user profile and preferences in parallel
    try {
      const [me, savedPrefs] = await Promise.all([
        api.get('/api/admin/me', accessToken),
        api.get('/api/admin/preferences', accessToken),
      ]);
      setUser(me);
      setPrefs({ ...DEFAULT_PREFS, ...savedPrefs });
    } catch {
      // non-fatal — defaults remain in place
    }
  }, [scheduleRefresh]);

  const silentRefresh = useCallback(async () => {
    try {
      const data = await api.get('/api/refresh');
      await applyTokens(data.access_token);
    } catch {
      setToken(null);
      setUser(null);
      setPrefs(DEFAULT_PREFS);
    }
  }, [applyTokens]);

  useEffect(() => {
    silentRefresh().finally(() => setLoading(false));
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Public API ────────────────────────────────────────────────────────────

  const login = useCallback(async (email, password) => {
    const data = await api.post('/api/authenticate', { email, password });
    await applyTokens(data.access_token);
  }, [applyTokens]);

  // Two-step passcode login — step 1: request a code be emailed.
  const requestPasscode = useCallback(async (email) => {
    await api.post('/api/request-passcode', { email });
  }, []);

  // Two-step passcode login — step 2: verify the code and establish a session.
  const loginWithPasscode = useCallback(async (email, passcode) => {
    const data = await api.post('/api/authenticate-passcode', { email, passcode });
    await applyTokens(data.access_token);
  }, [applyTokens]);

  const register = useCallback(async (email, firstName, lastName, cosename) => {
    const data = await api.post('/api/register', {
      email_address: email,
      first_name: firstName,
      last_name: lastName,
      nickname: cosename,
    });
    await applyTokens(data.access_token);
    // Mirror the browser's color-scheme preference on first sign-up
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      try {
        await api.patch('/api/admin/preferences', { theme: 'dark' }, data.access_token);
        setPrefs(p => ({ ...p, theme: 'dark' }));
      } catch { /* non-fatal — default light theme remains */ }
    }
  }, [applyTokens]);

  const logout = useCallback(async () => {
    try { await api.get('/api/logout'); } catch { /* ignore */ }
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setToken(null);
    setUser(null);
    setPrefs(DEFAULT_PREFS);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    const me = await api.get('/api/admin/me', token);
    setUser(me);
  }, [token]);

  // Called by PreferencesPage after a successful save so all pages see the new values
  const refreshPrefs = useCallback(async () => {
    if (!token) return;
    const saved = await api.get('/api/admin/preferences', token);
    setPrefs({ ...DEFAULT_PREFS, ...saved });
  }, [token]);

  return (
    <AuthContext.Provider value={{
      token, user, prefs, loading,
      login, requestPasscode, loginWithPasscode,
      register, logout,
      refreshUser, refreshPrefs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
